import { Button } from "@clockwise/design-system";
import { bg_info_emphasis, bg_positive_emphasis } from "@clockwise/design-system/tokens";
import { CollapseContent } from "@clockwise/icons";
import { LinearProgress, makeStyles } from "@material-ui/core";
import classNames from "classnames";
import pluralize from "pluralize";
import React, { useEffect } from "react";
import { ErrorBlanket } from "./atoms/ErrorBlanket";
import { EventCardMode } from "./types";

const useStyles = makeStyles({
  hideScrollbar: {
    scrollbarWidth: "none" /* Firefox */,
    msOverflowStyle: "none" /* IE 10+ */,
    "&::-webkit-scrollbar": {
      display: "none" /* Safari and Chrome */,
    },
  },
});

export const EventCardExpanded = ({
  children,
  onClose,
  mode,
  footer,
  flexSettingsView,
  rsvpModule,
  hasFetchingError = false,
  shouldShowRSVP = false,
  loading,
  eventColor,
  changesCount = 0,
}: {
  alsoSendUpdates?: boolean;
  children: React.ReactNode;
  onClose: () => void;
  flexSettingsView?: React.ReactNode;
  mode: EventCardMode;
  footer: React.ReactNode;
  rsvpModule?: React.ReactNode;
  hasFetchingError?: boolean;
  shouldShowRSVP?: boolean;
  loading: boolean;
  eventColor?: string;
  changesCount?: number;
}) => {
  const { hideScrollbar } = useStyles();

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []); //eslint-disable-line react-hooks/exhaustive-deps

  const getColorOfTopBar = () => {
    if (eventColor) {
      return eventColor;
    }
    if (loading) {
      return bg_positive_emphasis;
    }
    return bg_info_emphasis;
  };

  return (
    <div
      className="cw-z-40 cw-absolute cw-w-full cw-p-3 cw-h-full"
      role="modal"
      aria-label="Event Details"
    >
      <div
        style={{
          boxShadow:
            "0px 0px 0px 1px rgba(28, 40, 64, 0.02), 0px 1px 4px 0px rgba(28, 40, 64, 0.12), 0px 0px 0px 1px rgba(28, 40, 64, 0.04)",
        }}
        className="cw-relative cw-flex cw-flex-col cw-bg-default cw-overflow-hidden cw-h-full cw-border cw-rounded-lg cw-divide-y"
      >
        {flexSettingsView && (
          <div
            className={`${
              mode === EventCardMode.flexSettings
                ? "cw-translate-x-0"
                : "cw-translate-x-[100%] cw-invisible"
            } cw-transition-all cw-z-10 cw-duration-300 cw-overflow-y-scroll ec-agenda-full cw-rounded-lg cw-w-full cw-absolute cw-top-0 cw-bottom-0 cw-right-0 cw-bg-default`}
          >
            {flexSettingsView}
          </div>
        )}

        {hasFetchingError && (
          <ErrorBlanket
            hasError={hasFetchingError}
            onClose={onClose}
            message="Failed to fetch event details"
          />
        )}
        <div className="cw-h-[30px] cw-body-sm cw-text-12 cw-px-3 cw-bg-neutral cw-text-subtle cw-flex cw-justify-between cw-items-center">
          <div>Event</div>
          <div className="cw-flex cw-items-center cw-justify-center cw-gap-2">
            {Boolean(changesCount) && (
              <>
                <div>{`${changesCount} ${pluralize("updates", changesCount)}`}</div>
                <div className="cw-border-solid cw-border-l cw-border-subtle cw-h-2 cw-w-[1px]" />
              </>
            )}
            <Button
              size="mini"
              aria-label="Collapse"
              variant="text"
              sentiment="neutral"
              startIcon={CollapseContent}
              onClick={onClose}
            />
          </div>
        </div>
        <div style={{ backgroundColor: getColorOfTopBar() }} className="cw-h-1">
          {loading && <LinearProgress className="cw-h-1" />}
        </div>
        <div className={`cw-flex cw-flex-col cw-flex-1 cw-overflow-y-scroll ${hideScrollbar}`}>
          {children}
        </div>
        <div className="cw-bg-neutral cw-border-t cw-border-solid cw-border-muted">
          {shouldShowRSVP && rsvpModule && <div className="cw-py-2 cw-px-3">{rsvpModule}</div>}
          <div
            className={classNames({
              "cw-border-t cw-border-solid cw-border-muted": rsvpModule && shouldShowRSVP,
            })}
          >
            {footer}
          </div>
        </div>
      </div>
    </div>
  );
};
